import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import DashboardLayout from "@/components/DashboardLayout";
import { EmptyState, ErrorState } from "@/components/StateViews";
import { SignedImage } from "@/lib/signedFile";
import { toast } from "sonner";
import { Loader2, GraduationCap, Check, X, Send, Search } from "lucide-react";

const STATUS_META = {
  pending: { label: "قيد الرد", tone: "bg-amber-50 text-amber-700 border-amber-100" },
  accepted: { label: "مقبول", tone: "bg-emerald-50 text-emerald-700 border-emerald-100" },
  rejected: { label: "مرفوض", tone: "bg-red-50 text-red-700 border-red-100" },
  cancelled: { label: "ملغى تلقائياً", tone: "bg-slate-100 text-slate-600 border-slate-200" },
};

export default function SupervisionRequests() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [dirLoading, setDirLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState("");
  const [reply, setReply] = useState(null); // { req, action }
  const [replyNote, setReplyNote] = useState("");
  const [directory, setDirectory] = useState([]);
  const [q, setQ] = useState("");
  const [sending, setSending] = useState(false);
  const [note, setNote] = useState("");

  const loadRequests = async () => {
    setLoading(true); setError("");
    try {
      const { data } = await api.get("/supervision-requests");
      setItems(data.requests || []);
    } catch (e) { setError(formatApiError(e)); }
    finally { setLoading(false); }
  };
  const loadDirectory = async () => {
    setDirLoading(true);
    try {
      if (user?.role === "student") {
        const params = user?.university ? `?university=${encodeURIComponent(user.university)}` : "";
        const { data } = await api.get(`/faculty/directory${params}`);
        setDirectory(data.faculty || []);
      } else if (user?.role === "faculty") {
        const { data } = await api.get(`/faculty/students-directory`);
        setDirectory(data.students || []);
      }
    } catch (e) { /* silent */ }
    finally { setDirLoading(false); }
  };
  useEffect(() => { loadRequests(); loadDirectory(); /* eslint-disable-next-line */ }, []);

  const decide = async (req, action) => {
    setBusyId(req.id);
    try {
      await api.patch(`/supervision-requests/${req.id}`, { action, response_note: replyNote });
      toast.success(action === "accept" ? "تم قبول الطلب" : "تم رفض الطلب");
      setReply(null); setReplyNote("");
      loadRequests(); loadDirectory();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyId(""); }
  };

  const sendRequest = async (target) => {
    setSending(true);
    try {
      const body = user.role === "student"
        ? { faculty_id: target.id, note }
        : { student_id: target.id, note };
      await api.post("/supervision-requests", body);
      toast.success("تم إرسال طلب الإشراف");
      setNote("");
      loadRequests(); loadDirectory();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(false); }
  };

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return directory;
    return directory.filter((d) => (d.name || "").toLowerCase().includes(t) || (d.college || "").toLowerCase().includes(t) || (d.major || "").toLowerCase().includes(t));
  }, [directory, q]);

  const iAmTarget = (r) =>
    (user.role === "faculty" && r.initiator === "student" && r.faculty_id === user.id) ||
    (user.role === "student" && r.initiator === "faculty" && r.student_id === user.id);

  const dirTitle = user?.role === "student" ? "أعضاء هيئة التدريس في جامعتك" : "طلاب كليتك/جامعتك";
  const alreadyRequested = new Set(
    items.filter((r) => r.status === "pending").map((r) => (user.role === "student" ? r.faculty_id : r.student_id))
  );

  return (
    <DashboardLayout>
      <div data-testid="supervision-requests">
        <div className="mb-8">
          <div className="text-xs font-bold uppercase tracking-widest text-teal-700">الإشراف الأكاديمي</div>
          <h1 className="mt-2 text-2xl font-heading font-extrabold text-slate-900">
            {user?.role === "student" ? "مشرفي الأكاديمي" : "طلابي وطلبات الإشراف"}
          </h1>
          <p className="mt-2 text-slate-600 max-w-3xl">
            {user?.role === "student"
              ? "اربط نفسك بمشرف أكاديمي من جامعتك ليتابع تدريبك التعاوني ومهامك وشهاداتك. الربط لا يتم إلا بموافقة الطرفين، وتستطيع الإدارة إسناد مشرف مباشرةً."
              : "اقبل طلبات الطلاب الراغبين بإشرافك، أو ابعث طلباً لأحد طلاب كليتك ليتم ربطه بك بمجرد قبوله."}
          </p>
        </div>

        {/* Requests section */}
        <div className="mb-10">
          <h2 className="text-lg font-heading font-bold text-slate-900 mb-3">الطلبات</h2>
          {loading ? (
            <div className="grid place-items-center py-10"><Loader2 className="h-8 w-8 animate-spin text-teal-700" /></div>
          ) : error ? (
            <ErrorState message={error} onRetry={loadRequests} testid="sr-error" />
          ) : items.length === 0 ? (
            <EmptyState icon={GraduationCap} title="لا توجد طلبات إشراف بعد" description="يمكنك بدء طلب إشراف من قائمة الأسفل." testid="sr-empty" />
          ) : (
            <div className="grid gap-3">
              {items.map((r) => {
                const meta = STATUS_META[r.status] || { label: r.status, tone: "bg-slate-100" };
                const canDecide = r.status === "pending" && iAmTarget(r);
                return (
                  <div key={r.id} className="p-4 rounded-2xl bg-white border border-cream-200 card-shadow" data-testid={`sr-${r.id}`}>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <div className="text-xs text-slate-500">
                          {user.role === "student" ? "عضو التدريس" : "الطالب"}
                        </div>
                        <div className="font-heading font-bold text-slate-900">
                          {user.role === "student" ? r.faculty_name : r.student_name}
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5">
                          {r.initiator === "student" ? "بدأ الطلب: الطالب" : r.initiator === "faculty" ? "بدأ الطلب: عضو التدريس" : "بدأ الطلب: الإدارة"}
                        </div>
                      </div>
                      <span className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold border ${meta.tone}`}>{meta.label}</span>
                    </div>
                    {r.note && (
                      <div className="mt-3 p-3 rounded-lg bg-cream-50 text-sm text-slate-700 whitespace-pre-wrap">{r.note}</div>
                    )}
                    {r.response_note && (
                      <div className="mt-2 text-xs text-slate-500">الرد: <span className="text-slate-700">{r.response_note}</span></div>
                    )}
                    {canDecide && (
                      <div className="mt-4 flex items-center gap-2">
                        <button onClick={() => { setReply({ req: r, action: "accept" }); setReplyNote(""); }} disabled={busyId === r.id} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-teal-700 text-white text-sm font-semibold hover:bg-teal-600 disabled:opacity-60" data-testid={`sr-accept-${r.id}`}>
                          <Check className="h-4 w-4" /> قبول
                        </button>
                        <button onClick={() => { setReply({ req: r, action: "reject" }); setReplyNote(""); }} disabled={busyId === r.id} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-red-50 text-red-700 text-sm font-semibold hover:bg-red-100 disabled:opacity-60" data-testid={`sr-reject-${r.id}`}>
                          <X className="h-4 w-4" /> رفض
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Directory / start a new request */}
        <div>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <h2 className="text-lg font-heading font-bold text-slate-900">{dirTitle}</h2>
            <div className="relative">
              <Search className="h-4 w-4 text-slate-400 absolute inset-y-0 start-3 my-auto" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="بحث بالاسم أو التخصص" className="rounded-lg border border-slate-300 bg-white ps-9 pe-3 py-2 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none" data-testid="sr-search" />
            </div>
          </div>

          {user?.role === "student" && !user?.university && (
            <div className="mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm">
              أضف جامعتك في «الملف الشخصي» لتظهر لك قائمة أعضاء هيئة التدريس المتاحين.
            </div>
          )}

          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="رسالة اختيارية تُرفَق مع طلب الإشراف" className="w-full rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none mb-4" data-testid="sr-note" />

          {dirLoading ? (
            <div className="grid place-items-center py-10"><Loader2 className="h-8 w-8 animate-spin text-teal-700" /></div>
          ) : filtered.length === 0 ? (
            <EmptyState icon={GraduationCap} title="لا يوجد نتائج" description="جرّب توسيع البحث أو تحديث بيانات جامعتك/كليتك." testid="sr-dir-empty" />
          ) : (
            <div className="grid gap-3 md:grid-cols-2">
              {filtered.map((d) => {
                const isBoundToMe = user.role === "student" ? user.supervisor_id === d.id : d.is_mine;
                const requested = alreadyRequested.has(d.id);
                return (
                  <div key={d.id} className="p-4 rounded-2xl bg-white border border-cream-200 card-shadow flex items-start gap-3" data-testid={`sr-dir-${d.id}`}>
                    <div className="h-11 w-11 rounded-full bg-gradient-to-br from-teal-700 to-teal-900 grid place-items-center text-white font-bold overflow-hidden shrink-0">
                      {d.avatar_url ? <SignedImage fileId={d.avatar_url} alt="" className="h-full w-full object-cover" fallback={<span>{d.name?.[0] || "?"}</span>} /> : (d.name?.[0] || "?")}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-heading font-bold text-slate-900 truncate">{d.name}</div>
                      <div className="text-xs text-slate-500 truncate">
                        {[d.university, d.college || d.major].filter(Boolean).join(" · ")}
                      </div>
                      <div className="mt-3">
                        {isBoundToMe ? (
                          <span className="inline-flex px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold border border-emerald-100">✓ مرتبط بك حالياً</span>
                        ) : requested ? (
                          <span className="inline-flex px-3 py-1.5 rounded-full bg-amber-50 text-amber-700 text-xs font-semibold border border-amber-100">قيد الرد</span>
                        ) : (
                          <button onClick={() => sendRequest(d)} disabled={sending} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-teal-700 text-white text-xs font-semibold hover:bg-teal-600 disabled:opacity-60" data-testid={`sr-request-${d.id}`}>
                            <Send className="h-3.5 w-3.5" /> إرسال طلب إشراف
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {reply && (
          <div className="fixed inset-0 z-50 grid place-items-center bg-slate-900/60 backdrop-blur-sm p-4" onClick={(e) => { if (e.target === e.currentTarget) setReply(null); }}>
            <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl border border-slate-200" data-testid="sr-reply-modal">
              <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
                <h3 className="font-heading font-bold text-slate-900 text-lg">{reply.action === "accept" ? "قبول الطلب" : "رفض الطلب"}</h3>
                <button onClick={() => setReply(null)} className="p-1.5 rounded-lg hover:bg-slate-100"><X className="h-5 w-5" /></button>
              </div>
              <div className="p-4 space-y-4">
                <textarea value={replyNote} onChange={(e) => setReplyNote(e.target.value)} rows={3} placeholder="رسالة اختيارية (سبب/ترحيب)" className="w-full rounded-lg border border-slate-300 px-4 py-3 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none" data-testid="sr-reply-note" />
                <div className="flex justify-end gap-2">
                  <button onClick={() => setReply(null)} className="px-4 py-2 rounded-lg text-slate-700 hover:bg-slate-100">إلغاء</button>
                  <button onClick={() => decide(reply.req, reply.action)} disabled={busyId === reply.req.id} className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-white font-semibold disabled:opacity-60 ${reply.action === "accept" ? "bg-teal-700 hover:bg-teal-600" : "bg-red-600 hover:bg-red-500"}`} data-testid="sr-reply-confirm">
                    {busyId === reply.req.id ? <Loader2 className="h-4 w-4 animate-spin" /> : reply.action === "accept" ? <Check className="h-4 w-4" /> : <X className="h-4 w-4" />}
                    {reply.action === "accept" ? "قبول" : "رفض"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
